import * as i0 from '@angular/core';
import { Component, HostBinding, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@simpl/element-ng/content-action-bar';
import { SiContentActionBarModule } from '@simpl/element-ng/content-action-bar';
import * as i3 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';
import { PortalModule } from '@angular/cdk/portal';

class SiCardComponent {
    // Needed for inheritance.
    get classElevation() {
        return true;
    }
    /**
     * Card header text.
     */
    heading;
    /**
     * Input list of primary action items. Supports up to **4** actions and omits additional ones.
     */
    primaryActions = [];
    /**
     * Input list of secondary action items.
     */
    secondaryActions = [];
    /**
     * A param that will be passed to the `action` in the primary/secondary actions.
     * This allows to re-use the same primary/secondary action arrays across rows
     * in a table.
     */
    actionParam;
    /**
     * The view type of the content action bar of the card. Default is `collapsible`
     * for dashboards. However, in some cases you might need to change to `expanded`
     * or `mobile`.
     */
    actionBarViewType = 'collapsible';
    /**
     * Optional setting of html title attribute for the content action bar.
     * Helpful for a11y when only one action is configured in expand mode.
     */
    actionBarTitle = '';
    /**
     * Returns `true` when primary or secondary actions are set.
     */
    get displayContentActionBar() {
        return this.primaryActions?.length > 0 || this.secondaryActions?.length > 0;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiCardComponent, selector: "si-card", inputs: { heading: "heading", primaryActions: "primaryActions", secondaryActions: "secondaryActions", actionParam: "actionParam", actionBarViewType: "actionBarViewType", actionBarTitle: "actionBarTitle" }, host: { properties: { "class.elevation-1": "this.classElevation" }, classAttribute: "card" }, ngImport: i0, template: "<div class=\"card-header d-flex justify-content-between\">\n  <ng-content #cardHeaderIcon select=\"[headerIcon]\"></ng-content>\n  <div class=\"text-truncate\">{{ heading | translate }}</div>\n  <div class=\"cab d-flex ms-6 my-n4 me-n5\">\n    <si-content-action-bar\n      *ngIf=\"displayContentActionBar\"\n      class=\"ms-auto\"\n      [primaryActions]=\"primaryActions\"\n      [secondaryActions]=\"secondaryActions\"\n      [actionParam]=\"actionParam\"\n      [viewType]=\"actionBarViewType\"\n      [attr.title]=\"actionBarTitle | translate\"\n    >\n    </si-content-action-bar>\n  </div>\n</div>\n<ng-content #cardBody select=\"[body]\"></ng-content>\n", styles: [".card-header{block-size:40px}.text-truncate{flex:0 1 auto}.cab{flex:1 0 0;min-inline-size:40px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.SiContentActionBarComponent, selector: "si-content-action-bar", inputs: ["primaryActions", "secondaryActions", "actionParam", "viewType", "focusOnLoad", "toggleItemLabel", "preventIconsInDropdownMenus", "disabled"] }, { kind: "pipe", type: i3.SiTranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-card', host: {
                        class: 'card'
                    }, template: "<div class=\"card-header d-flex justify-content-between\">\n  <ng-content #cardHeaderIcon select=\"[headerIcon]\"></ng-content>\n  <div class=\"text-truncate\">{{ heading | translate }}</div>\n  <div class=\"cab d-flex ms-6 my-n4 me-n5\">\n    <si-content-action-bar\n      *ngIf=\"displayContentActionBar\"\n      class=\"ms-auto\"\n      [primaryActions]=\"primaryActions\"\n      [secondaryActions]=\"secondaryActions\"\n      [actionParam]=\"actionParam\"\n      [viewType]=\"actionBarViewType\"\n      [attr.title]=\"actionBarTitle | translate\"\n    >\n    </si-content-action-bar>\n  </div>\n</div>\n<ng-content #cardBody select=\"[body]\"></ng-content>\n", styles: [".card-header{block-size:40px}.text-truncate{flex:0 1 auto}.cab{flex:1 0 0;min-inline-size:40px}\n"] }]
        }], propDecorators: { classElevation: [{
                type: HostBinding,
                args: ['class.elevation-1']
            }], heading: [{
                type: Input,
                args: [{ required: true }]
            }], primaryActions: [{
                type: Input
            }], secondaryActions: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], actionBarViewType: [{
                type: Input
            }], actionBarTitle: [{
                type: Input
            }] } });

class SiCardModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiCardModule, declarations: [SiCardComponent], imports: [CommonModule, PortalModule, SiContentActionBarModule, SiTranslateModule], exports: [SiCardComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCardModule, imports: [CommonModule, PortalModule, SiContentActionBarModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule, SiContentActionBarModule, SiTranslateModule],
                    declarations: [SiCardComponent],
                    exports: [SiCardComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiCardComponent, SiCardModule };
